import { FormattedMessage, useIntl } from 'react-intl';
import LocOfferCard from '../Assets/LocOfferCard';
import TermLoanOfferCard from '../Assets/TermLoanOfferCard';
import { Button, Card, FormattedMessageDiv, FormCheckbox, Navigation, useWizardContext } from '@odmonorepo/core-components';
import { Stack } from 'react-bootstrap';
import React, { useEffect } from 'react';
import { Application, CheckoutData, Offer } from 'merchant-api/types';
import { useFormContext } from 'react-hook-form';
import { completeCheckout } from 'merchant-api/checkout';

export interface TermsAndAgreementsFormProps {
  checkoutData: CheckoutData,
  selectedOffer: Offer,
  applicationData: Application,
  openDisclosurePdf: () => void,
  openContractPdf: () => void,
  isLoadingDoc?: boolean
}

export const TermsAndAgreementsForm = ({ checkoutData, selectedOffer, applicationData, openDisclosurePdf, openContractPdf, isLoadingDoc }: TermsAndAgreementsFormProps) => {

  const { setValue, trigger, clearErrors } = useFormContext();
  const { triggerComplete, isCompleteError } = completeCheckout()
  const { nextStep, stepOrder, getCurrentStepIndex } = useWizardContext();
  const nextStepKey = stepOrder[getCurrentStepIndex() + 1];

  const hasDisclosure = !!checkoutData?.disclosureUuid;

  setValue('hasDisclosure', hasDisclosure);
  if ( isCompleteError ) throw new Error('Failed to complete checkout');

  return (
    <Stack gap={ 4 } style={{ maxWidth: '600px' }} className="mx-auto">

      <h2 className="text-center fs-3">
        <FormattedMessage id="termsAndAgreements.title" />
      </h2>
      { selectedOffer.product === 'LineOfCredit' ? (
        <LocOfferCard offer={ selectedOffer } checkout={ checkoutData } />
      ) : (
        <TermLoanOfferCard offer={ selectedOffer } applicationType={ applicationData.type } showTermsAndAgreementsMessage />
      )}

      { hasDisclosure && (
        <Card style={{ maxWidth: '600px' }} className="mx-auto" bodyClassName="p-md-5 px-md-8 p-5">
          <FormattedMessageDiv className='h4' id="termsAndAgreements.disclosureTitle"/>

          <FormattedMessageDiv className='pb-4'id="termsAndAgreements.disclosureInstructions" values={{ strong: <strong>you must open and review</strong> }} />
           <Card className="bg-light-gray shadow-none text-center" bodyClassName='p-4'>
            <FormattedMessageDiv className="fw-bold fs-7 pb-2" id={`termsAndAgreements.disclosureDocument${selectedOffer.product === 'LineOfCredit' ? 'LOC' : ''}`} />
            <Button
              className="rounded-pill mt-2 py-2 px-5"
              variant="outline-primary"
              onClick={ () => { openDisclosurePdf(); setValue('openedDisclosure', true); clearErrors(); } }
              loading={ isLoadingDoc }
            >
              <FormattedMessageDiv className="fs-7" id="termsAndAgreements.openDocument"/>
            </Button>
          </Card>

          <FormCheckbox name="disclosureCheckbox" className="fs-6 mt-4" label={ <FormattedMessage id='termsAndAgreements.disclosureCheckbox'/> } useYupErrors onInput={() => trigger('disclosureCheckbox')}/>
        </Card>
      )}

      <Card style={{ maxWidth: '600px' }} className="mx-auto" bodyClassName="p-md-5 px-md-8 p-5">
        <div>
          <h4>
            { selectedOffer.product === 'LineOfCredit' ? (
              <FormattedMessage id="termsAndAgreements.agreementHeadingLOC" />
            ) : (
              <FormattedMessage id="termsAndAgreements.agreementHeading" />
            )}
          </h4>

          <FormattedMessageDiv id="termsAndAgreements.agreementInstructions" className='mb-4' />

          <Card className="bg-light-gray shadow-none text-center" bodyClassName='p-4'>
            <FormattedMessageDiv className="fw-bold fs-7 pb-2" id={`termsAndAgreements.agreementTitle${selectedOffer.product === 'LineOfCredit' ? 'LOC' : ''}`} />
            <Button
              className="rounded-pill mt-2 py-2 px-5"
              variant="outline-primary"
              onClick={() => openContractPdf()}
              loading={isLoadingDoc}
            >
              <FormattedMessageDiv className="fs-7" id="termsAndAgreements.openDocument"/>
            </Button>
          </Card>

          <FormCheckbox name="termsCheckbox1" useYupErrors required className="fs-6 mt-4" label={
            selectedOffer.product === 'LineOfCredit' ? (
              <FormattedMessage id="termsAndAgreements.checkbox1LOC" />
            ) : (
              <FormattedMessage id="termsAndAgreements.checkbox1" />
            )}
          />

          <FormCheckbox name="termsCheckbox2" useYupErrors required className="fs-6 mt-4" label={
            <FormattedMessage id="termsAndAgreements.checkbox2" />
          }
          />

          <FormCheckbox name="termsCheckbox3" useYupErrors required className="fs-6 mt-4" label={
            <FormattedMessageDiv id="termsAndAgreements.checkbox3" values={{
              esignConsent: (txt: any) => <a href="https://www.ondeck.com/esignagreement" target='_blank' rel='noreferrer'>{txt}</a>,
              creditCheckLink: (txt: any) => <a href="https://www.ondeck.com/creditagreement" target='_blank' rel='noreferrer'>{txt}</a>,
              privacyLink: (txt: any) => <a href="https://www.ondeck.com/privacy" target='_blank' rel='noreferrer'>{txt}</a>
            }} /> }
          />

          <FormCheckbox name="termsCheckbox4" useYupErrors required className="fs-6 mt-4" label={
            <FormattedMessage id="termsAndAgreements.checkbox4" />
          }
          />

        </div>
      </Card>

      <Navigation className="mt-4 mb-2" continueOnly={selectedOffer.product === 'LineOfCredit' ? false : true} continueText={nextStepKey === 'PLAID_LAUNCHER' ? 'Agree and Continue' : 'Agree and Get Funds'} handleNext={() => triggerComplete().then(() => nextStep()).catch(() => { throw new Error('Failed to complete checkout'); })}/>

    </Stack>
  );
}

export default TermsAndAgreementsForm;
